import React from 'react';
import {Stack} from '@mui/material';
import BasicSearchFiltersPanel from '../reportEditorPage/leftPanel/components/BasicSearchFiltersPanel';
import {useProductsFilters} from '../hooks/useProductsFilters';
import {getNestedValue} from '../utils/formatters';

/**
 * Derives unique string values for each filterable field from the loaded reports.
 * ponytail: done once per reports array change via useMemo; cheap for typical list sizes.
 */
function usePropertyOptions(reports, filterableFields) {
    return React.useMemo(() => {
        return filterableFields.map(({key, label}) => {
            const values = Array.from(
                new Set(
                    reports
                        .map((r) => {
                            const v = getNestedValue(r, key);
                            return v !== null && v !== undefined ? String(v).trim() : null;
                        })
                        .filter(Boolean),
                ),
            ).sort((a, b) => a.localeCompare(b));

            return {key, label, values};
        });
    }, [reports, filterableFields]);
}

/**
 * Horizontal filter bar for Products listing pages.
 * Accepts raw reports + filterable field definitions; returns filteredReports.
 *
 * Usage:
 *   const { filteredReports, filtersBar } = useFiltersRow(reports, DRAFTS_FILTER_FIELDS);
 *   // render filtersBar above the table
 */
export function useFiltersRow(reports, filterableFields, {onFilterChange, baselineRows, lockedKeys} = {}) {
    const defaultKeys = React.useMemo(() => filterableFields.map((f) => f.key), [filterableFields]);
    const filters = useProductsFilters(reports, defaultKeys, baselineRows);
    const propertyOptions = usePropertyOptions(reports, filterableFields);

    // Memoised so its identity is stable between renders when nothing changed.
    // Avoids downstream React.memo() bailouts being defeated by a new JSX object reference.
    const filtersBar = React.useMemo(() => (
        <BasicSearchFiltersPanel
            rows={filters.rows}
            propertyOptions={propertyOptions}
            defaultKeys={defaultKeys}
            lockedKeys={lockedKeys}
            onToggleMoreFilter={filters.onToggleMoreFilter}
            onOperatorChange={filters.onOperatorChange}
            onToggleValue={filters.onToggleValue}
            onClearValues={filters.onClearValues}
            onRemoveFilter={filters.onRemoveFilter}
            onFilterChange={onFilterChange}
        />
    ), [
        filters.rows,
        filters.onToggleMoreFilter,
        filters.onOperatorChange,
        filters.onToggleValue,
        filters.onClearValues,
        filters.onRemoveFilter,
        propertyOptions,
        defaultKeys,
        lockedKeys,
        onFilterChange,
    ]);

    return {filteredReports: filters.filteredReports, filtersBar, applyFilterRows: filters.applyFilterRows, filterRows: filters.rows};
}

/** Filterable fields shared by Drafts and Fintel pages */
export const DRAFTS_FILTER_FIELDS = [
    {key: 'userDisplayName', label: 'Owner'},
    {key: 'productType.label', label: 'Product Type'},
    {key: 'status', label: 'Status'},
];

/** Filterable fields for the Disclosures page */
export const DISCLOSURES_FILTER_FIELDS = [
    {key: 'userDisplayName', label: 'Owner'},
    {key: 'status', label: 'Status'},
];

/**
 * Standalone wrapper component — use when you prefer JSX composition over hook.
 * Renders the filter bar + passes filteredReports as a render prop.
 */
const FiltersRow = ({reports, filterableFields, children}) => {
    const {filteredReports, filtersBar} = useFiltersRow(reports, filterableFields);

    return (
        <Stack spacing={2}>
            {filtersBar}
            {children(filteredReports)}
        </Stack>
    );
};

export default FiltersRow;
